import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid,
  MenuItem, Select, Stack, Tab, Tabs, TextField, Typography,
  Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import {
  TenantVM, fetchTenant, setTenantStatus, updateTenant, impersonateTenant,
  fetchTenantCustomers, fetchTenantOrders, fetchTenantProducts, fetchTenantStaff,
} from "../data/api";
import { useAuth } from "../auth/AuthContext";
import { formatCAD } from "../utils/currency";

const STATUSES = ["TRIAL", "ACTIVE", "SUSPENDED", "DISABLED"];
const fmt = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—");

const TenantDetailPage: React.FC = () => {
  const { id } = useParams();
  const tid = Number(id);
  const navigate = useNavigate();
  const { enterImpersonation } = useAuth();

  const [tenant, setTenant] = useState<TenantVM | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [edit, setEdit] = useState({ name: "", contactEmail: "", primaryContact: "", slug: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  // drill-down data
  const [customers, setCustomers] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [loadingTab, setLoadingTab] = useState(false);

  const loadTenant = useCallback(async () => {
    setLoading(true);
    try {
      const t = await fetchTenant(tid);
      setTenant(t);
      if (t) setEdit({ name: t.name || "", contactEmail: t.contactEmail || "", primaryContact: t.primaryContact || "", slug: t.slug || "" });
    } finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { loadTenant(); }, [loadTenant]);

  useEffect(() => {
    if (!tenant) return;
    (async () => {
      setLoadingTab(true);
      try {
        if (tab === 1 && customers.length === 0) setCustomers(await fetchTenantCustomers(tid));
        else if (tab === 2 && orders.length === 0) setOrders(await fetchTenantOrders(tid));
        else if (tab === 3 && products.length === 0) setProducts(await fetchTenantProducts(tid));
        else if (tab === 4 && staff.length === 0) setStaff(await fetchTenantStaff(tid));
      } finally { setLoadingTab(false); }
    })();
  }, [tab, tenant, tid]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (s: string) => {
    setSavingStatus(true); setErr(null);
    try { await setTenantStatus(tid, s); await loadTenant(); }
    catch (e: any) { setErr(e?.response?.data?.message || "Could not change status."); }
    finally { setSavingStatus(false); }
  };

  const saveEdit = async () => {
    setSavingEdit(true); setErr(null);
    try { await updateTenant(tid, edit); await loadTenant(); }
    catch (e: any) { setErr(e?.response?.data?.message || "Could not save company details."); }
    finally { setSavingEdit(false); }
  };

  const viewAs = async () => {
    setErr(null);
    try {
      const res = await impersonateTenant(tid);
      enterImpersonation(res.token, res.tenant?.name || tenant?.name || "tenant");
      navigate("/dashboard");
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "Could not start tenant inspection.");
    }
  };

  if (loading) return <Box sx={{ display: "grid", placeItems: "center", py: 10 }}><CircularProgress /></Box>;
  if (!tenant) return (
    <Box><Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/tenants")} sx={{ mb: 2 }}>Back</Button>
      <Alert severity="warning">Tenant not found.</Alert></Box>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/tenants")}>All tenants</Button>
        <Button startIcon={<VisibilityIcon />} variant="contained" onClick={viewAs}>View as tenant (read-only)</Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
        <Typography variant="h4">{tenant.name}</Typography>
        <Chip label={tenant.status} size="small" />
        {tenant.slug && <Typography variant="body2" color="text.secondary">/{tenant.slug}</Typography>}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
        {["Overview", "Customers", "Orders", "Products", "Staff"].map((l) => <Tab key={l} label={l} />)}
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card><CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>At a glance</Typography>
              <Grid container spacing={2}>
                {[["Users", tenant.userCount], ["Buyers", tenant.buyerCount], ["Products", tenant.productCount], ["Orders", tenant.orderCount]].map(([l, v]) => (
                  <Grid size={{ xs: 6 }} key={l as string}>
                    <Typography variant="caption" color="text.secondary">{l}</Typography>
                    <Typography variant="h6">{v ?? 0}</Typography>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Activation</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Select size="small" value={tenant.status} disabled={savingStatus}
                  onChange={(e) => changeStatus(e.target.value)} sx={{ minWidth: 160 }}>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
                {savingStatus && <CircularProgress size={18} />}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Created {fmt(tenant.createdAt)}
              </Typography>
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card><CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Company details</Typography>
              <Stack spacing={2}>
                <TextField label="Company name" size="small" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                <TextField label="Slug" size="small" value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} />
                <TextField label="Primary contact" size="small" value={edit.primaryContact} onChange={(e) => setEdit({ ...edit, primaryContact: e.target.value })} />
                <TextField label="Contact email" size="small" value={edit.contactEmail} onChange={(e) => setEdit({ ...edit, contactEmail: e.target.value })} />
                <Button variant="outlined" onClick={saveEdit} disabled={savingEdit} sx={{ alignSelf: "flex-start" }}>
                  {savingEdit ? <CircularProgress size={18} /> : "Save details"}
                </Button>
              </Stack>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {tab !== 0 && (
        <Card><CardContent>
          {loadingTab ? <Box sx={{ display: "grid", placeItems: "center", py: 5 }}><CircularProgress /></Box> : (
            <Box sx={{ overflowX: "auto" }}>
              {tab === 1 && <SimpleTable head={["Name", "Email", "Phone", "Status"]} rows={customers.map((u) => [u.name, u.email, u.phone, u.status])} empty="No customers." />}
              {tab === 2 && <SimpleTable head={["Order #", "Customer", "Items", "Total", "Status"]} rows={orders.map((o) => [o.orderId, o.customerName, o.itemCount, formatCAD(Number(o.totalAmount || 0)), o.status])} empty="No orders." />}
              {tab === 3 && <SimpleTable head={["Product", "SKU", "Price", "Stock"]} rows={products.map((p) => [p.name, p.sku || "—", formatCAD(Number(p.price || 0)), p.stock ?? 0])} empty="No products." />}
              {tab === 4 && <SimpleTable head={["Name", "Email", "Role", "Status"]} rows={staff.map((u) => [u.name, u.email, u.role, u.status])} empty="No staff." />}
            </Box>
          )}
        </CardContent></Card>
      )}
    </Box>
  );
};

const SimpleTable: React.FC<{ head: string[]; rows: any[][]; empty: string }> = ({ head, rows, empty }) => {
  if (!rows.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>{empty}</Typography>;
  return (
    <Table size="small">
      <TableHead><TableRow>{head.map((h) => <TableCell key={h} sx={{ color: "text.secondary", fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
      <TableBody>
        {rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j}>{c ?? "—"}</TableCell>)}</TableRow>)}
      </TableBody>
    </Table>
  );
};

export default TenantDetailPage;
